import React, { useMemo } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { formatDateSlashes, sortByDate } from '~/services/TimeService';

import type { SalesforceSurchargePeriod } from '~/types/surcharges';

import { daysOfWeekMessage } from '~/utils/daysOfWeek';
import { getUnmarkedOverlappingSurcharges } from '~/utils/surchargeOverlapAnalysis';

import QuickSearchToolbar from './QuickSearchToolbar';

interface Props {
  salesforceSurcharges?: Array<SalesforceSurchargePeriod>;
  selectedSurcharges?: Array<SalesforceSurchargePeriod>;
  onSelectSurcharges: (surchargeIds: Array<string | number>) => void;
}

export function SurchargesSelection({ salesforceSurcharges = [], selectedSurcharges = [], onSelectSurcharges }: Props) {
  const columns: Array<GridColDef<SalesforceSurchargePeriod>> = [
    {
      field: 'name',
      headerName: 'Surcharge Period',
      minWidth: 150,
      flex: 1,
      display: 'flex',
    },
    {
      field: 'start_date',
      headerName: 'From',
      minWidth: 100,
      type: 'date',
      sortComparator: sortByDate,
      valueFormatter: (value) => formatDateSlashes(value),
      display: 'flex',
    },
    {
      field: 'end_date',
      headerName: 'To',
      minWidth: 100,
      type: 'date',
      sortComparator: sortByDate,
      valueFormatter: (value) => formatDateSlashes(value),
      display: 'flex',
    },
    {
      field: 'daysOfWeek',
      headerName: 'Days of week',
      minWidth: 150,
      flex: 1,
      valueGetter: (_value, row) => daysOfWeekMessage(row),
      display: 'flex',
    },
    {
      field: 'accumulate',
      headerName: 'Accumulate',
      minWidth: 110,
      display: 'flex',
      renderCell: (params) => (
        <Chip
          label={params.row.accumulate ? 'Yes' : 'No'}
          size="small"
          color={params.row.accumulate ? 'primary' : 'default'}
          variant={params.row.accumulate ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'overlapping',
      headerName: 'Overlapping',
      minWidth: 110,
      display: 'flex',
      renderCell: (params) => <Chip label={params.row.overlapping ? 'Yes' : 'No'} size="small" variant="outlined" />,
    },
    {
      field: 'cost_price',
      headerName: 'Cost',
      maxWidth: 100,
      display: 'flex',
    },
    {
      field: 'sell_price',
      headerName: 'Sell',
      maxWidth: 90,
      display: 'flex',
    },
  ];

  const unmarkedOverlapping = useMemo(() => getUnmarkedOverlappingSurcharges(selectedSurcharges), [selectedSurcharges]);

  return (
    <Box sx={{ height: '100%' }}>
      {unmarkedOverlapping.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {unmarkedOverlapping.length} selected{' '}
          {unmarkedOverlapping.length === 1 ? 'surcharge overlaps' : 'surcharges overlap'} in date range and weekdays
          but {unmarkedOverlapping.length === 1 ? 'is' : 'are'} not marked as overlapping in Salesforce. Please review
          before proceeding.
        </Alert>
      )}
      <DataGrid
        rows={salesforceSurcharges}
        columns={columns}
        density="compact"
        pageSizeOptions={[10]}
        sx={{
          '.MuiDataGrid-menuIcon': {
            width: 'auto',
            visibility: 'visible',
          },
          '.MuiDataGrid-iconButtonContainer': {
            visibility: 'visible',
            width: 'auto',
            '&:not(.MuiDataGrid-columnHeader--sorted) .MuiDataGrid-sortIcon': {
              opacity: 1,
            },
          },
          minWidth: 650,
          height: '100%',
        }}
        checkboxSelection
        onRowSelectionModelChange={(rowSelectionModel) => onSelectSurcharges([...rowSelectionModel])}
        rowSelectionModel={selectedSurcharges.map((s) => s.id)}
        slots={{ toolbar: QuickSearchToolbar }}
      />
    </Box>
  );
}
